import React, { useState } from 'react';
import { Users, Loader2, Plus, AlertCircle, CheckCircle2, LogOut } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface CompanyProps {
  profile: any;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

interface CompanyRegistrationData {
  company_name: string;
  industry: string;
  address: string;
  contact_email: string;
  contact_phone: string;
  access_key: string;
  user_id?: string;
}

interface ServerResponse {
  response: 'success' | 'error';
  description: string;
}

export const Company: React.FC<CompanyProps> = ({ profile, setError, setSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [registrationData, setRegistrationData] = useState<CompanyRegistrationData>({
    company_name: '',
    industry: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    access_key: ''
  });
  
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/auth');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleUnlinkCompany = async () => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          id_company: null, 
          role: null,
        })
        .eq('id', profile?.id);

      if (error) throw error;
      window.location.reload(); // Reload to refresh profile
    } catch (error) {
      console.error('Error unlinking company:', error);
      setError('Failed to unlink from company');
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingUser(true);
    setError(null);

    try {
      // Now add the user with the company ID only
      const { data, error } = await supabase
        .from('profiles')
        .update({ 
          id_company: profile?.id_company
        })
        .eq('email', newUserEmail)
        .select();

      if (error) throw error;
      if (data.length === 0) throw new Error('User not found');

      setNewUserEmail('');
      setSuccess('Usuario agregado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error adding user:', error);
      setError('Error al agregar usuario. Porfavor verifica haber introducido su correo correctamente');
    } finally {
      setAddingUser(false);
    }
  };

  const validateField = (value: string): boolean => {
    // Check for special characters except @ and . for email
    const specialCharsRegex = /[!#$%^&*()_+\-=\[\]{};':"\\|,<>/?]/;
    return !specialCharsRegex.test(value);
  };

  const validateForm = (): string | null => {
    // Check for empty fields
    for (const [key, value] of Object.entries(registrationData)) {
      if (!value.trim()) {
        return `El campo ${key.replace('_', ' ')} es obligatorio`;
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registrationData.contact_email)) {
      return 'El formato del email de contacto no es válido';
    }

    // Validate phone format (allow numbers, spaces, and + symbol)
    const phoneRegex = /^[0-9\s+]+$/;
    if (!phoneRegex.test(registrationData.contact_phone)) {
      return 'El teléfono solo puede contener números, espacios y el símbolo +';
    }

    // Check for special characters in other fields
    const fieldsToValidate: Array<'company_name' | 'industry' | 'address' | 'access_key'> = ['company_name', 'industry', 'address', 'access_key'];
    for (const field of fieldsToValidate) {
      if (!validateField(registrationData[field])) {
        return `El campo ${field.replace('_', ' ')} no puede contener caracteres especiales`;
      }
    }

    return null;
  };

  const handleRegisterCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      const dataWithUserId = {
        ...registrationData,
        user_id: profile?.id
      };

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/a753f1d4-503b-4f4e-be4e-6d03bd26d671', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataWithUserId),
      });

      const data: ServerResponse = await response.json();

      if (data.response === 'error') {
        setError(data.description);
        return;
      }

      if (data.response === 'success') {
        setSuccess('Empresa registrada exitosamente');
        setTimeout(() => {
          setShowRegistrationForm(false);
          setSuccess(null);
          window.location.reload(); // Reload to refresh profile
        }, 2000);
      }
    } catch (error) {
      console.error('Error registering company:', error);
      setError('Error al registrar la empresa. Por favor, inténtalo nuevamente.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm mb-6">
      <div className="p-6 border-b">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-gray-700" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Empleados</h2>
            <p className="text-sm text-gray-500">
              Gestiona la vinculación con la empresa y tu equipo
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            {profile?.id_company ? (
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-md font-medium text-gray-900">
                        {profile.company_name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Actualmente estás vinculado a esta empresa.
                      </p>
                    </div>
                    <button
                      onClick={handleUnlinkCompany}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Desvincular
                    </button>
                  </div>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-md font-medium text-gray-900 mb-3">
                    Agregar usuario a la empresa
                  </h3>
                  <form onSubmit={handleAddUser} className="flex gap-2">
                    <div className="flex-grow">
                      <input
                        type="email"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        placeholder="Correo electrónico del usuario"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={addingUser}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {addingUser ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        'Agregar'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {showRegistrationForm ? (
                  <form onSubmit={handleRegisterCompany} className="space-y-4">
                    <div>
                      <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre de la Empresa
                      </label>
                      <input
                        type="text"
                        id="company_name"
                        value={registrationData.company_name}
                        onChange={(e) => setRegistrationData({...registrationData, company_name: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
                        Industria
                      </label>
                      <input
                        type="text"
                        id="industry"
                        value={registrationData.industry}
                        onChange={(e) => setRegistrationData({...registrationData, industry: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                        Dirección
                      </label>
                      <input
                        type="text"
                        id="address"
                        value={registrationData.address}
                        onChange={(e) => setRegistrationData({...registrationData, address: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email de Contacto
                      </label>
                      <input
                        type="email"
                        id="contact_email"
                        value={registrationData.contact_email}
                        onChange={(e) => setRegistrationData({...registrationData, contact_email: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Teléfono de Contacto
                      </label>
                      <input
                        type="tel"
                        id="contact_phone"
                        value={registrationData.contact_phone}
                        onChange={(e) => setRegistrationData({...registrationData, contact_phone: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="access_key" className="block text-sm font-medium text-gray-700 mb-1">
                        Clave de Acceso
                      </label>
                      <input
                        type="text"
                        id="access_key"
                        value={registrationData.access_key}
                        onChange={(e) => setRegistrationData({...registrationData, access_key: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div className="flex gap-4 mt-6">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-2"
                      >
                        {loading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-blue-500" />
                        )}
                        Registrar Empresa
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowRegistrationForm(false)}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="flex flex-col items-center py-8">
                    <AlertCircle className="w-12 h-12 text-blue-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No estás vinculado a ninguna empresa
                    </h3>
                    <p className="text-gray-500 text-center mb-6">
                      Para continuar, puedes registrar una nueva empresa o solicitar acceso a una existente.
                    </p>
                    <div className="space-x-4">
                      <button
                        onClick={() => setShowRegistrationForm(true)}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Registrar Nueva Empresa
                      </button>
                      <button
                        onClick={handleLogout}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors flex items-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        Cerrar Sesión
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};